import React from 'react';
import { DealDetail } from '../../../types';
import { Customer } from '../../../types';
import { DEAL_STAGES } from '../utils/dealStages';

interface DealEditFormProps {
  selectedDeal: DealDetail;
  customers: Customer[];
  panelError: string | null;
  submitting: boolean;
  handleDealChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  setSelectedDeal: (updater: (prev: DealDetail | null) => DealDetail | null) => void;
  setViewMode: (mode: 'view' | 'edit' | null) => void;
  handleUpdateDeal: (e: React.FormEvent) => void;
}

const DealEditForm: React.FC<DealEditFormProps> = ({
  selectedDeal,
  customers,
  panelError,
  submitting,
  handleDealChange,
  setSelectedDeal,
  setViewMode,
  handleUpdateDeal,
}) => {
  return (
    <form onSubmit={handleUpdateDeal} className="space-y-4">
      {panelError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {panelError}
        </div>
      )}

      <div className="rounded-xl theme-border theme-card p-4">
        <label className="block text-sm font-medium theme-text-primary mb-1">
          Deal Name <span className="text-red-500">*</span>
        </label>
        <input
          name="name"
          value={selectedDeal.name}
          onChange={handleDealChange}
          required
          className="w-full theme-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent theme-bg-primary theme-text-primary"
          placeholder="Enter deal name"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-xl theme-border theme-card p-4">
          <label className="block text-sm font-medium theme-text-primary mb-1">
            Customer
          </label>
          <select
            name="customer_id"
            value={selectedDeal.customer_id || ''}
            onChange={(e) => {
              setSelectedDeal((prev) => prev ? { ...prev, customer_id: e.target.value ? Number(e.target.value) : null } : null);
            }}
            className="w-full theme-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent theme-bg-primary theme-text-primary"
          >
            <option value="">Select Customer</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.item}
              </option>
            ))}
          </select>
        </div>
        <div className="rounded-xl theme-border theme-card p-4">
          <label className="block text-sm font-medium theme-text-primary mb-1">
            Type
          </label>
          <input
            name="type"
            value={selectedDeal.type || ''}
            onChange={handleDealChange}
            className="w-full theme-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent theme-bg-primary theme-text-primary"
            placeholder="Enter type"
          />
        </div>
      </div>

      <div className="rounded-xl theme-border theme-card p-4">
        <label className="block text-sm font-medium theme-text-primary mb-1">Stage</label>
        <select
          name="stage"
          value={selectedDeal.stage}
          onChange={handleDealChange}
          className="w-full theme-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent theme-bg-primary theme-text-primary"
        >
          {DEAL_STAGES.map((stage) => (
            <option key={stage} value={stage}>
              {stage}
            </option>
          ))}
        </select>
      </div>

      <div className="rounded-xl theme-border theme-card p-4">
        <label className="block text-sm font-medium theme-text-primary mb-1">
          Notes
        </label>
        <textarea
          name="notes"
          value={selectedDeal.notes || ''}
          onChange={handleDealChange}
          rows={4}
          className="w-full theme-border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent theme-bg-primary theme-text-primary"
          placeholder="Enter notes (optional)"
        />
      </div>

      <div className="flex gap-3 pt-4">
        <button
          type="button"
          onClick={() => setViewMode('view')}
          className="flex-1 px-4 py-2 theme-border rounded-lg theme-bg-secondary theme-text-primary hover:opacity-80 transition-opacity"
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:shadow-lg transition-shadow disabled:opacity-50"
        >
          {submitting ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
};

export default DealEditForm;
